import axios from "axios";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import imagenesEditProject from "../../../assets/imgEditProject/imgEditProject";
import Cookies from 'universal-cookie';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useNavigate, Link } from "react-router-dom";
import IconLoading from "../../iconLoading/IconLoading";
import Select from 'react-select';

interface Specialty {
    id: number;
    name: string;
    created_at: string;
    updated_at: string;
    // Áreas que solo se llenan automáticamente desde Hirint (no se autodeclaran).
    validated_only?: boolean;
}

interface Knowledge {
    id: number;
    name: string;
    type_id: number;
}

interface KnowledgeOption {
    id: string;
    name: string;
}

interface KnowledgeLevel {
    knowledge_id: string;
    level_tier: number;
}

// Mejoras Chambaticon §A.1/§B.1: 4 niveles con etiqueta en vez de un slider de porcentaje.
interface LevelTier {
    level_tier: number;
    label: string;
    description: string;
}

const initialStateSpecialties: Specialty[] = [];
const initialStateKnowledge: Knowledge[] = [];

const SpecialtiesKnowledge = () => {
    const [specialties, setSpecialties] = useState<Specialty[]>(initialStateSpecialties);
    const [knowledgeOptions, setKnowledgeOptions] = useState<KnowledgeOption[]>([]);
    const [selectedKnowledge, setSelectedKnowledge] = useState<KnowledgeLevel[]>([]);
    const [levelTiers, setLevelTiers] = useState<LevelTier[]>([]);
    const [idUser, setIdUser] = useState<number>(0);
    const [sendingData, setSendingData] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [knowledgeEmpty, setKnowledgeEmpty] = useState(false);
    // La lista de habilidades depende de la especialización: sin elegir una primero se veía
    // vacía ("No hay opciones disponibles") sin decir por qué.
    const [hasSpecialtySelected, setHasSpecialtySelected] = useState(false);
    const [loadingKnowledge, setLoadingKnowledge] = useState(false);
    const MySwal = withReactContent(Swal);
    const navigate = useNavigate();
    const cookies = new Cookies();
    const getSesion = cookies.get("Bearer");

    useEffect(() => {
        async function getSpecialties() {
            const urlCountry = `${process.env.REACT_APP_API_URI}Knowledge-specialization/list/types`;
            let config = {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'ApiToken': `Bearer ${getSesion.token}`
                }
            };
            try {
                const resp = await axios.get(urlCountry, config);
                setSpecialties((resp.data.specializationTypes || []).filter((specialty: Specialty) => !specialty.validated_only));
            } catch (err: any) {
                console.error(err);
                setLoadError(
                    err.response?.status === 401
                        ? 'Tu sesión expiró. Vuelve a iniciar sesión para ver las especialidades.'
                        : 'No se pudieron cargar las especialidades. Verifica tu conexión e intenta de nuevo.'
                );
            }
        };
        getSpecialties();

        async function getLevelTiers() {
            const urlLevels = `${process.env.REACT_APP_API_URI}Knowledge-specialization/list/levels`;
            try {
                const resp = await axios.get(urlLevels);
                setLevelTiers(resp.data.data);
            } catch (err) {
                console.error(err);
                setLoadError('No se pudieron cargar los niveles disponibles. Intenta de nuevo más tarde.');
            }
        };
        getLevelTiers();

        const getIdUser = () => {
            const { id } = cookies.get('Bearer');
            setIdUser(id)
        }
        getIdUser();
    }, []);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        sendData();
    };

    const sendData = () => {
        if (selectedKnowledge.length === 0) {
            MySwal.fire({
                position: 'center',
                icon: 'warning',
                title: 'Por favor selecciona al menos una habilidad',
                showConfirmButton: false,
                timer: 3500
            });
            return;
        }

        if (selectedKnowledge.some(item => !item.level_tier)) {
            MySwal.fire({
                position: 'center',
                icon: 'warning',
                title: 'Elige un nivel para cada habilidad seleccionada',
                showConfirmButton: false,
                timer: 3500
            });
            return;
        }

        setSendingData(true);
        const url = `${process.env.REACT_APP_API_URI}freelancer/register/Knowledge-specializations`;

        let config = {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'ApiToken': `Bearer ${getSesion.token}`
            }
        }

        const payload = {
            'user_id': idUser,
            'knowledges': selectedKnowledge
        };

        axios.post(url, payload, config)
            .then(response => {
                MySwal.fire({
                    position: 'center',
                    icon: 'success',
                    title: response.data.message,
                    showConfirmButton: false,
                    timer: 3500
                }).then(() => {
                    setSendingData(false);
                    navigate('/perfil-freelancer');
                });
            })
            .catch(err => {
                MySwal.fire({
                    position: 'center',
                    icon: 'error',
                    title: err.response?.status === 403
                        ? 'Una o más competencias seleccionadas ya están validadas por una evaluación externa y no pueden modificarse.'
                        : (err.response?.data?.message || 'Error al guardar los datos'),
                    showConfirmButton: false,
                    timer: 3500
                });
                setSendingData(false);
            });
    };

    const handleSpecialtyChange = (selectedOptions: any) => {
        if (!selectedOptions || selectedOptions.length === 0) {
            setHasSpecialtySelected(false);
            setKnowledgeOptions([]);
            setSelectedKnowledge([]);
            setKnowledgeEmpty(false);
            return;
        }

        setHasSpecialtySelected(true);
        const specialtyIds = selectedOptions.map((opt: any) => opt.value).join(',');
        fetchKnowledgeOptions(specialtyIds);
    };

    const fetchKnowledgeOptions = async (specialtyIds: string) => {
        const url = `${process.env.REACT_APP_API_URI}Knowledge-specialization/list/Knowledge/${specialtyIds}`;
        const config = {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'ApiToken': `Bearer ${getSesion.token}`,
            }
        };

        setLoadingKnowledge(true);
        try {
            const resp = await axios.get(url, config);
            const options = resp.data.data.map((item: any) => ({
                id: item.id.toString(),
                name: item.name
            }));
            setKnowledgeOptions(options);
            setKnowledgeEmpty(options.length === 0);
            setLoadError('');
        } catch (err: any) {
            console.error(err);
            setKnowledgeOptions([]);
            setKnowledgeEmpty(false);
            setLoadError(
                err.response?.status === 401
                    ? 'Tu sesión expiró. Vuelve a iniciar sesión para ver las habilidades.'
                    : 'No se pudieron cargar las habilidades de esa especialidad. Intenta de nuevo.'
            );
        } finally {
            setLoadingKnowledge(false);
        }
    };

    const handleKnowledgeChange = (selectedOptions: any) => {
        if (!selectedOptions || selectedOptions.length === 0) {
            setSelectedKnowledge([]);
            return;
        }

        const newSelected = selectedOptions.map((opt: any) => ({
            knowledge_id: opt.value,
            level_tier: 0
        }));

        const mergedSelections = newSelected.map((newItem: KnowledgeLevel) => {
            const existing = selectedKnowledge.find(item => item.knowledge_id === newItem.knowledge_id);
            return existing || newItem;
        });

        setSelectedKnowledge(mergedSelections);
    };

    const handleTierChange = (knowledgeId: string, levelTier: number) => {
        setSelectedKnowledge(prev =>
            prev.map(item =>
                item.knowledge_id === knowledgeId
                    ? { ...item, level_tier: levelTier }
                    : item
            )
        );
    };

    const specialtySelectOptions = specialties.map(specialty => ({
        value: specialty.id,
        label: specialty.name
    }));

    const knowledgeSelectOptions = knowledgeOptions.map(option => ({
        value: option.id,
        label: option.name
    }));

    const selectedKnowledgeIds = selectedKnowledge.map(item => item.knowledge_id);

    return (
        <div className="w-full md:w-95% flex justify-center">
            <form className="w-full md:w-95%" onSubmit={handleSubmit}>
                <div className="flex justify-center pb-0 lg:pb-12">
                    <h2 className="text-Yellow text-2xl md:text-3xl text-center font-semibold py-2 md:py-8">
                        Registrar especialidad
                    </h2>
                </div>

                {loadError && (
                    <div className="mb-4 text-center text-red-300 bg-red-900/30 border border-red-700 rounded px-4 py-2 text-sm">
                        {loadError}
                    </div>
                )}

                <div className="mb-6 text-start">
                    <label htmlFor="" className="text-white columns-1">
                        Selecciona tu/s especialización/es
                    </label>
                    <span className="text-gray-400 text-xs italic block">
                        Puedes seleccionar todas las habilidades que posees
                    </span>
                    
                    <div className="flex flex-wrap justify-center py-4">
                        <div className="w-full py-2">
                            <Select
                                isMulti
                                name="specialties"
                                options={specialtySelectOptions}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                placeholder="Selecciona una o varias especializaciones"
                                noOptionsMessage={() => 'No hay opciones disponibles'}
                                onChange={handleSpecialtyChange}
                            />
                        </div>
                        
                        <div className="w-full py-2">
                            <Select
                                isMulti
                                name="knowledges"
                                options={knowledgeSelectOptions}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                placeholder={hasSpecialtySelected ? "Selecciona una o varias habilidades" : "Primero selecciona una especialización"}
                                isDisabled={!hasSpecialtySelected}
                                isLoading={loadingKnowledge}
                                loadingMessage={() => 'Cargando habilidades...'}
                                noOptionsMessage={() => knowledgeEmpty ? 'Ya registraste todas las habilidades de esta especialización' : 'No hay habilidades que coincidan'}
                                onChange={handleKnowledgeChange}
                                value={knowledgeSelectOptions.filter(option =>
                                    selectedKnowledgeIds.includes(option.value)
                                )}
                            />
                        </div>

                        {knowledgeEmpty && !loadError && (
                            <p className="w-full text-center text-gray-400 text-xs italic mt-1">
                                Ya registraste todas las competencias disponibles en la/s especialización/es seleccionada/s.
                            </p>
                        )}
                    </div>
                </div>
                
                {selectedKnowledge.length > 0 && (
                    <div className="mb-6 text-start">
                        <label htmlFor="" className="text-white columns-1">
                            Establece tu nivel en cada habilidad
                        </label>
                        <div className="flex flex-wrap border-solid border border-indigo-600 rounded mt-2 p-4">
                            {selectedKnowledge.map((item, index) => {
                                const knowledge = knowledgeOptions.find(opt => opt.id === item.knowledge_id);
                                const selectedTier = levelTiers.find(lvl => lvl.level_tier === item.level_tier);
                                return (
                                    <div key={index} className="w-full px-2 py-4">
                                        <div className="flex justify-between mb-2">
                                            <span className="text-white">{knowledge?.name}</span>
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                            {levelTiers.map(lvl => (
                                                <button
                                                    type="button"
                                                    key={lvl.level_tier}
                                                    onClick={() => handleTierChange(item.knowledge_id, lvl.level_tier)}
                                                    className={`px-2 py-2 rounded-lg text-sm font-medium border transition duration-150 ease-in-out ${item.level_tier === lvl.level_tier
                                                        ? 'bg-Yellow text-white border-Yellow'
                                                        : 'bg-transparent text-gray-300 border-gray-500 hover:border-Yellow'
                                                        }`}
                                                >
                                                    {lvl.label}
                                                </button>
                                            ))}
                                        </div>
                                        {selectedTier && (
                                            <p className="text-xs text-gray-400 italic mt-2">
                                                {selectedTier.description}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="columns-1 flex justify-center">
                    <div className="text-center lg:text-left w-full md:w-1/2">
                        <button
                            type="submit"
                            className="inline-block px-7 py-3 bg-Yellow text-white font-medium text-sm leading-snug uppercase rounded-lg shadow-md hover:bg-amber-600 hover:shadow-lg focus:bg-amber-700 focus:shadow-lg focus:outline-none focus:ring-0 active:bg-amber-800 active:shadow-lg transition duration-150 ease-in-out w-full"
                            disabled={sendingData}>
                            {!sendingData ? "Guardar Cambios" : <IconLoading />}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default SpecialtiesKnowledge;